/**
 * The Invoice builder — hourly work for a month, grouped into a few described
 * lines and rounded, the way it actually goes to a client.
 *
 * Opens on the saved drafts. A new draft picks a client and month (and,
 * optionally, one of the client's projects), and starts grouped by task, so
 * the work is merging and renaming lines rather than building from nothing.
 * On the left are the month's entries not yet on a line; on the right is the
 * invoice as it prints. Everything saves as you go.
 *
 * Client, month and project are fixed once a draft exists. Changing them would
 * leave lines full of entries from the old scope; a second draft is clearer.
 *
 * The rules — rounding, amounts, which entries are in reach — are in
 * lib/invoiceDraft and tested there. Entries are never edited here except to
 * stamp them invoiced, exactly as the Invoice attachment does.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { card } from "@/components/atoms";
import { ReportFooter, ReportMasthead, reportPaper, reportTable, reportTd, reportTh } from "@/components/ReportMasthead";
import { useToast } from "@/context/ToastContext";
import { useAllItems, useProjects } from "@/hooks/useData";
import { useInvoiceDraftMutations, useInvoiceDrafts } from "@/hooks/useInvoiceDrafts";
import { useClients, useTimeEntries, useTimeMutations } from "@/hooks/useTime";
import {
  ROUND_STEPS, autoGroup, billedHours, draftPool, draftTotals, entryMonth, lineAmount, lineRate,
  loggedSeconds, moveEntries, reconcile, unassigned, type GroupBy,
} from "@/lib/invoiceDraft";
import { clock, deriveEntryLabel, entryDayKey, entryKind, projectName } from "@/lib/timeRollup";
import { useIsMobile } from "@/lib/useMediaQuery";
import type { InvoiceDraft, InvoiceLine, TimeEntry } from "@/lib/types";

const GROUPINGS: [GroupBy | "none", string][] = [
  ["task", "By task"], ["activity", "By activity"], ["entry", "One line per entry"], ["none", "Start empty"],
];

const monthLabel = (m: string) =>
  new Date(`${m}-01T00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

/** Invoices are to the cent, unlike the whole-dollar figures in Reports. */
const cents = (n: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2 }).format(n);
const hrs = (n: number) => n.toFixed(2);

/** Shared lookups: what an entry is called, and whether it is billable time. */
function useEntryContext() {
  const { data: entries = [], isSuccess: entriesLoaded } = useTimeEntries();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();
  const taskTitle = useMemo(() => new Map(items.map((i) => [i.id, i.title])), [items]);
  const byId = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);
  const isHourly = (e: TimeEntry) => entryKind(e, projects) === "hourly";
  const label = (e: TimeEntry) => deriveEntryLabel(e.note, taskTitle.get(e.item ?? ""));
  return { entries, entriesLoaded, projects, clients, taskTitle, byId, isHourly, label };
}

export function InvoiceBuilder({ onClose }: { onClose: () => void }) {
  const { data: drafts = [], isLoading, isError } = useInvoiceDrafts();
  const [openId, setOpenId] = useState<string | null>(null);
  const open = drafts.find((d) => d.id === openId);

  return (
    <div className="print-sheet" style={{ position: "fixed", inset: 0, zIndex: 70, overflowY: "auto", background: "var(--bg)" }}>
      {open
        ? <DraftEditor key={open.id} initial={open} drafts={drafts} onBack={() => setOpenId(null)} onClose={onClose} />
        : <DraftList drafts={drafts} loading={isLoading} failed={isError} onOpen={setOpenId} onClose={onClose} />}
    </div>
  );
}

/* -- The list of drafts, and starting a new one ------------------------------ */

function DraftList({ drafts, loading, failed, onOpen, onClose }: {
  drafts: InvoiceDraft[];
  loading: boolean;
  failed: boolean;
  onOpen: (id: string) => void;
  onClose: () => void;
}) {
  const ctx = useEntryContext();
  const { createDraft, deleteDraft } = useInvoiceDraftMutations();
  const toast = useToast();

  /** Billable, unbilled time on no draft yet — what a new invoice could be built from. */
  const open = useMemo(() => {
    const drafted = new Set(drafts.flatMap((d) => d.lines.flatMap((l) => l.entryIds)));
    return ctx.entries.filter((e) => ctx.isHourly(e) && !e.invoicedAt && !drafted.has(e.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.entries, ctx.projects, drafts]);
  const months = useMemo(() => [...new Set(open.map(entryMonth))].sort().reverse(), [open]);
  const [month, setMonth] = useState("");
  const pickedMonth = month || months[0] || "";
  const clientIds = useMemo(
    () => [...new Set(open.filter((e) => entryMonth(e) === pickedMonth && e.client).map((e) => e.client!))],
    [open, pickedMonth],
  );
  const [clientId, setClientId] = useState("");
  const pickedClient = clientIds.includes(clientId) ? clientId : clientIds[0] ?? "";
  const projectIds = useMemo(
    () => [...new Set(open.filter((e) => entryMonth(e) === pickedMonth && e.client === pickedClient && e.project).map((e) => e.project!))],
    [open, pickedMonth, pickedClient],
  );
  const [projectId, setProjectId] = useState("");
  const pickedProject = projectIds.includes(projectId) ? projectId : "";
  const [grouping, setGrouping] = useState<GroupBy | "none">("task");

  const clientName = (id?: string) => ctx.clients.find((c) => c.id === id)?.name ?? "";

  async function start() {
    if (!pickedMonth || !pickedClient) return;
    const shell = { id: "", month: pickedMonth, client: pickedClient, project: pickedProject, lines: [] as InvoiceLine[] };
    const { pool } = draftPool(ctx.entries, shell, drafts, ctx.isHourly);
    const lines = grouping === "none" ? [] : autoGroup(pool, grouping, {
      task: (id) => ctx.taskTitle.get(id),
      project: (id) => projectName(ctx.projects, id),
      entry: ctx.label,
    });
    const project = pickedProject ? projectName(ctx.projects, pickedProject) : "";
    try {
      const row = await createDraft.mutateAsync({
        title: `${clientName(pickedClient)}${project ? ` · ${project}` : ""} — ${monthLabel(pickedMonth)}`,
        month: pickedMonth, client: pickedClient, project: pickedProject,
        roundTo: 0.25, roundMode: "nearest", showRates: true, invoiceNumber: "", status: "draft", lines,
      });
      onOpen(row.id);
    } catch {
      toast("Could not create the draft — has pb_schema_invoices.json been imported?");
    }
  }

  return (
    <>
      <div className="no-print" style={bar}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 16, fontWeight: "var(--fw-title)" }}>Invoice builder</span>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Hourly work, grouped into lines for the client</span>
          <button onClick={onClose} style={{ ...ghost, marginLeft: "auto" }}>Close</button>
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: 860, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
        <section style={{ ...card, padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: "var(--fw-title)" }}>New invoice</span>
          {months.length === 0 ? (
            <span style={{ fontSize: 13, color: "var(--muted)" }}>No billable time waiting on an invoice that isn't already on a draft below.</span>
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select value={pickedMonth} onChange={(e) => setMonth(e.target.value)} aria-label="Month" style={input}>
                {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
              <select value={pickedClient} onChange={(e) => setClientId(e.target.value)} aria-label="Client" style={input}>
                {clientIds.length === 0 && <option value="">No client with billable time</option>}
                {clientIds.map((id) => <option key={id} value={id}>{clientName(id) || "Unnamed client"}</option>)}
              </select>
              <select value={pickedProject} onChange={(e) => setProjectId(e.target.value)} aria-label="Project" style={input}>
                <option value="">All their projects</option>
                {projectIds.map((id) => <option key={id} value={id}>{projectName(ctx.projects, id)}</option>)}
              </select>
              <select value={grouping} onChange={(e) => setGrouping(e.target.value as GroupBy | "none")} aria-label="Start grouped" style={input}>
                {GROUPINGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <button onClick={() => void start()} disabled={!pickedClient || createDraft.isPending} style={primary}>
                {createDraft.isPending ? "Creating…" : "Start invoice"}
              </button>
            </div>
          )}
        </section>

        <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted2)" }}>Saved</span>
          {loading && <span style={{ fontSize: 13, color: "var(--muted)" }}>Loading…</span>}
          {failed && (
            <span style={{ fontSize: 13, color: "var(--warn)" }}>
              Could not load drafts. Import pocketbase/pb_schema_invoices.json into PocketBase to add the collection.
            </span>
          )}
          {!loading && !failed && drafts.length === 0 && (
            <span style={{ fontSize: 13, color: "var(--muted)" }}>Nothing saved yet.</span>
          )}
          {drafts.map((d) => {
            const t = draftTotals(d.lines, d, ctx.byId);
            const currency = d.lines.flatMap((l) => l.entryIds).map((id) => ctx.byId.get(id)?.currency).find(Boolean) ?? "USD";
            return (
              <div key={d.id} className="row-hover" onClick={() => onOpen(d.id)} style={{
                ...card, padding: "12px 16px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
              }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14, color: "var(--ink)" }}>{d.title}</span>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    {d.lines.length} {d.lines.length === 1 ? "line" : "lines"}
                    {d.invoiceNumber ? ` · ${d.invoiceNumber}` : ""}
                    {` · updated ${new Date(d.updated).toLocaleDateString()}`}
                  </span>
                </span>
                <span className="mono" style={{ fontSize: 13 }}>{hrs(t.billedHours)} h</span>
                {t.amount > 0 && <span className="mono" style={{ fontSize: 13, width: 96, textAlign: "right" }}>{cents(t.amount, currency)}</span>}
                <span style={{ ...badge, ...(d.status === "invoiced" ? badgeDone : null) }}>
                  {d.status === "invoiced" ? "Invoiced" : "Draft"}
                </span>
                {d.status !== "invoiced" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Delete the draft "${d.title}"? Its entries stay as they are.`)) return;
                      void deleteDraft.mutateAsync(d.id).catch(() => toast("Could not delete that draft"));
                    }}
                    title="Delete draft"
                    aria-label="Delete draft"
                    style={{ border: "none", background: "none", color: "var(--muted3)", cursor: "pointer", fontSize: 13 }}
                  >✕</button>
                )}
              </div>
            );
          })}
        </section>
      </div>
    </>
  );
}

/* -- One draft --------------------------------------------------------------- */

function DraftEditor({ initial, drafts, onBack, onClose }: {
  initial: InvoiceDraft;
  drafts: InvoiceDraft[];
  onBack: () => void;
  onClose: () => void;
}) {
  const ctx = useEntryContext();
  const mobile = useIsMobile();
  const toast = useToast();
  const { saveDraft } = useInvoiceDraftMutations();
  const { setInvoiced } = useTimeMutations();

  // The draft is edited locally and saved behind you; the server copy is only
  // read once, on open, so a save landing never overwrites what you just typed.
  const [d, setD] = useState<InvoiceDraft>(initial);
  const edit = (patch: Partial<InvoiceDraft>) => setD((cur) => ({ ...cur, ...patch }));
  const setLines = (fn: (lines: InvoiceLine[]) => InvoiceLine[]) => setD((cur) => ({ ...cur, lines: fn(cur.lines) }));

  /* Autosave: 700 ms after the last change, and once more on the way out. */
  const [saveState, setSaveState] = useState<"saved" | "pending" | "saving" | "failed">("saved");
  const latest = useRef(d);
  const dirty = useRef(false);
  const first = useRef(true);
  const persist = () => {
    if (!dirty.current) return;
    dirty.current = false;
    const { id, title, roundTo, roundMode, showRates, invoiceNumber, status, invoicedAt, lines } = latest.current;
    setSaveState("saving");
    saveDraft.mutateAsync({ id, title, roundTo, roundMode, showRates, invoiceNumber, status, invoicedAt: invoicedAt ?? "", lines })
      .then(() => setSaveState(dirty.current ? "pending" : "saved"))
      .catch(() => { dirty.current = true; setSaveState("failed"); });
  };
  useEffect(() => {
    latest.current = d;
    if (first.current) { first.current = false; return; }
    dirty.current = true;
    setSaveState("pending");
    const t = setTimeout(persist, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => persist(), []);

  const { pool, elsewhere } = useMemo(
    () => draftPool(ctx.entries, d, drafts, ctx.isHourly),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.entries, ctx.projects, d.lines, drafts],
  );
  // Entries deleted since the draft was saved fall off their lines. Only once
  // the entries have loaded — before then every id looks deleted.
  useEffect(() => {
    if (!ctx.entriesLoaded) return;
    const next = reconcile(d.lines, pool);
    if (next !== d.lines) setLines(() => next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.entriesLoaded, pool]);

  const loose = useMemo(() => unassigned(pool, d.lines), [pool, d.lines]);
  const totals = draftTotals(d.lines, d, ctx.byId);
  const currency = pool.find((e) => e.currency)?.currency ?? "USD";
  const invoiced = d.status === "invoiced";
  const clientName = ctx.clients.find((c) => c.id === d.client)?.name ?? "";
  const project = d.project ? projectName(ctx.projects, d.project) : "";

  /* Selection on the left: entries not on any line. */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const pickedIds = loose.filter((e) => picked.has(e.id)).map((e) => e.id);
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  function groupPicked() {
    if (pickedIds.length === 0) return;
    const firstEntry = ctx.byId.get(pickedIds[0])!;
    const suggestion = ctx.taskTitle.get(firstEntry.item ?? "") || ctx.label(firstEntry);
    const description = window.prompt("Description for this line", suggestion);
    if (description === null) return;
    setLines((ls) => moveEntries(ls, pickedIds, { description: description.trim() || suggestion }));
    setPicked(new Set());
  }

  function addPickedTo(lineId: string) {
    if (!lineId || pickedIds.length === 0) return;
    setLines((ls) => moveEntries(ls, pickedIds, { lineId }));
    setPicked(new Set());
  }

  function regroup(by: GroupBy | "none") {
    if (d.lines.length > 0 && !window.confirm("Replace every line with a fresh grouping? Descriptions and hand-set hours are lost.")) return;
    setLines(() => (by === "none" ? [] : autoGroup(pool, by, {
      task: (id) => ctx.taskTitle.get(id),
      project: (id) => projectName(ctx.projects, id),
      entry: ctx.label,
    })));
  }

  const updateLine = (id: string, patch: Partial<InvoiceLine>) =>
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const removeLine = (id: string) => setLines((ls) => ls.filter((l) => l.id !== id));
  const shiftLine = (id: string, dir: -1 | 1) => setLines((ls) => {
    const i = ls.findIndex((l) => l.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ls.length) return ls;
    const next = [...ls];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const mergeLine = (from: string, into: string) => setLines((ls) => {
    const src = ls.find((l) => l.id === from);
    if (!src) return ls;
    return moveEntries(ls, src.entryIds, { lineId: into }).filter((l) => l.id !== from);
  });

  /* Marking invoiced stamps the entries on the lines, as the attachment does. */
  const [confirming, setConfirming] = useState(false);
  const onLines = useMemo(() => d.lines.flatMap((l) => l.entryIds).map((id) => ctx.byId.get(id)).filter(Boolean) as TimeEntry[], [d.lines, ctx.byId]);
  const toStamp = onLines.filter((e) => !e.invoicedAt);

  async function markInvoiced() {
    const ids = toStamp.map((e) => e.id);
    const number = (d.invoiceNumber ?? "").trim();
    const when = new Date().toISOString();
    try {
      await setInvoiced.mutateAsync({ ids, invoicedAt: when, invoiceNumber: number });
      edit({ status: "invoiced", invoicedAt: when });
      setConfirming(false);
      toast(`Marked ${ids.length} ${ids.length === 1 ? "entry" : "entries"} invoiced${number ? ` on ${number}` : ""}`);
    } catch {
      toast("Some entries could not be marked — try again");
    }
  }

  async function reopen() {
    if (!window.confirm("Reopen this invoice? Its entries go back to not invoiced.")) return;
    try {
      await setInvoiced.mutateAsync({ ids: onLines.map((e) => e.id), invoicedAt: null });
      edit({ status: "draft", invoicedAt: "" });
    } catch {
      toast("Could not reopen — some entries may still be marked");
    }
  }

  function exportCsv() {
    const q = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const header = ["Description", "Hours", "Logged hours", "Rate", "Amount", "Currency"];
    const rows = printable.map((l) => {
      const rate = lineRate(l, ctx.byId);
      return [
        q(l.description), hrs(billedHours(l, d, ctx.byId)), hrs(loggedSeconds(l, ctx.byId) / 3600),
        rate === null ? "" : String(rate), lineAmount(l, d, ctx.byId).toFixed(2), q(currency),
      ].join(",");
    });
    const url = URL.createObjectURL(new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${d.title.replace(/[^\w]+/g, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // A line with nothing on it and no hand-set hours has nothing to bill.
  const printable = d.lines.filter((l) => l.entryIds.length > 0 || (l.hours ?? 0) > 0);
  const [openLine, setOpenLine] = useState<string | null>(null);

  return (
    <>
      {/* -- Controls ------------------------------------------------------- */}
      <div className="no-print" style={bar}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button onClick={onBack} style={ghost}>‹ All invoices</button>
          <input
            value={d.title}
            onChange={(e) => edit({ title: e.target.value })}
            aria-label="Invoice title"
            style={{ ...input, flex: "1 1 260px", fontSize: 14, fontWeight: 600 }}
          />
          <span style={{ fontSize: 12, color: saveState === "failed" ? "var(--warn)" : "var(--muted)", minWidth: 64 }}>
            {saveState === "saved" ? "Saved" : saveState === "failed" ? "Not saved" : "Saving…"}
          </span>
          <button onClick={exportCsv} style={ghost}>Export CSV</button>
          <button onClick={() => window.print()} style={primary}>Print / Save PDF</button>
          {invoiced
            ? <button onClick={() => void reopen()} style={ghost}>Reopen</button>
            : (
              <button
                onClick={() => setConfirming((v) => !v)}
                disabled={toStamp.length === 0}
                title={toStamp.length === 0 ? "No entries on the lines yet" : "Record that these hours have been invoiced"}
                style={{ ...ghost, ...(toStamp.length === 0 ? { opacity: 0.5, cursor: "not-allowed" } : null) }}
              >Mark as invoiced</button>
            )}
          <button onClick={onClose} style={ghost}>Close</button>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 12.5, color: "var(--ink3)" }}>
          <span>{clientName}{project ? ` · ${project}` : ""} · {monthLabel(d.month)}</span>
          <span style={{ width: 1, height: 18, background: "var(--line2)" }} />
          <label style={inline}>Round to
            <select value={d.roundTo ?? 0} onChange={(e) => edit({ roundTo: Number(e.target.value) })} style={input}>
              {ROUND_STEPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          {(d.roundTo ?? 0) > 0 && (
            <select value={d.roundMode ?? "nearest"} onChange={(e) => edit({ roundMode: e.target.value as "nearest" | "up" })} aria-label="Rounding direction" style={input}>
              <option value="nearest">nearest</option>
              <option value="up">always up</option>
            </select>
          )}
          <label style={inline}>
            <input type="checkbox" checked={d.showRates !== false} onChange={(e) => edit({ showRates: e.target.checked })} />
            Rates and amounts
          </label>
          <input
            value={d.invoiceNumber ?? ""}
            onChange={(e) => edit({ invoiceNumber: e.target.value })}
            placeholder="Invoice # (optional)"
            aria-label="Invoice number"
            style={{ ...input, width: 150 }}
          />
          {!invoiced && (
            <select value="" onChange={(e) => { if (e.target.value) regroup(e.target.value as GroupBy | "none"); }} aria-label="Regroup" style={input}>
              <option value="">Regroup…</option>
              {GROUPINGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
        </div>

        {confirming && toStamp.length > 0 && (
          <div style={notice}>
            <span style={{ fontSize: 12.5, color: "var(--ink2)" }}>
              Mark {toStamp.length} {toStamp.length === 1 ? "entry" : "entries"} invoiced
              {(d.invoiceNumber ?? "").trim() ? ` on ${(d.invoiceNumber ?? "").trim()}` : ""}.
              {loose.length > 0 && ` ${loose.length} not on the invoice will stay unbilled.`}
            </span>
            <button onClick={() => void markInvoiced()} disabled={setInvoiced.isPending} style={primary}>
              {setInvoiced.isPending ? "Marking…" : "Mark invoiced"}
            </button>
            <button onClick={() => setConfirming(false)} style={ghost}>Cancel</button>
          </div>
        )}
        {invoiced && (
          <div style={{ ...notice, background: "var(--goodTint)", border: "1px solid var(--line)" }}>
            <span style={{ fontSize: 12.5, color: "var(--goodInk)" }}>
              Invoiced {d.invoicedAt ? new Date(d.invoicedAt).toLocaleDateString() : ""}. Changes here edit this record only — the entries stay stamped.
            </span>
          </div>
        )}
      </div>

      <div className="ib-grid" style={{
        display: "grid", gridTemplateColumns: mobile ? "1fr" : "minmax(280px, 360px) 1fr",
        gap: 20, padding: "20px 24px 60px", alignItems: "start",
      }}>
        {/* -- Entries not on a line yet --------------------------------------- */}
        <section className="no-print" style={{ ...card, padding: 14, display: "flex", flexDirection: "column", gap: 8, position: mobile ? undefined : "sticky", top: 150 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13.5, fontWeight: "var(--fw-title)" }}>Not on the invoice</span>
            <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
              {loose.length} · {clock(loose.reduce((n, e) => n + (e.durationSeconds || 0), 0))}
            </span>
          </div>
          {loose.length === 0 ? (
            <span style={{ fontSize: 12.5, color: "var(--muted)" }}>Every entry is on a line.</span>
          ) : (
            <>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button onClick={groupPicked} disabled={pickedIds.length === 0} style={{ ...primary, ...(pickedIds.length === 0 ? disabledStyle : null) }}>
                  Group {pickedIds.length || ""} as a line
                </button>
                {d.lines.length > 0 && (
                  <select value="" onChange={(e) => addPickedTo(e.target.value)} disabled={pickedIds.length === 0} aria-label="Add to line" style={{ ...input, maxWidth: 160 }}>
                    <option value="">Add to line…</option>
                    {d.lines.map((l) => <option key={l.id} value={l.id}>{l.description || "Untitled line"}</option>)}
                  </select>
                )}
                <button
                  onClick={() => setPicked(pickedIds.length === loose.length ? new Set() : new Set(loose.map((e) => e.id)))}
                  style={{ ...ghost, padding: "6px 10px" }}
                >{pickedIds.length === loose.length ? "None" : "All"}</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", maxHeight: "60vh", overflowY: "auto" }}>
                {loose.map((e) => (
                  <label key={e.id} style={{ display: "flex", gap: 8, padding: "7px 2px", borderBottom: "1px solid var(--tint2)", cursor: "pointer", alignItems: "flex-start" }}>
                    <input type="checkbox" checked={picked.has(e.id)} onChange={() => toggle(e.id)} style={{ marginTop: 3 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13, color: "var(--ink)" }}>{ctx.label(e)}</span>
                      <span style={{ fontSize: 11.5, color: "var(--muted)" }}>
                        {shortDay(entryDayKey(e))} · {projectName(ctx.projects, e.project)}
                        {e.item && ctx.taskTitle.get(e.item) ? ` · ${ctx.taskTitle.get(e.item)}` : ""}
                      </span>
                    </span>
                    <span className="mono" style={{ fontSize: 12.5 }}>{clock(e.durationSeconds)}</span>
                  </label>
                ))}
              </div>
            </>
          )}
          {elsewhere > 0 && (
            <span style={{ fontSize: 11.5, color: "var(--muted)" }}>
              {elsewhere} more {elsewhere === 1 ? "entry is" : "entries are"} on another draft for this period.
            </span>
          )}
        </section>

        {/* -- The invoice ------------------------------------------------------ */}
        <div className="prose print-page" style={{ ...card, ...reportPaper, maxWidth: 820, width: "100%", color: "var(--ink)", boxShadow: "var(--shadowCard)" }}>
          <ReportMasthead
            kind="Services"
            title={clientName || "Hourly work"}
            subtitle={project || undefined}
            meta={[
              ["Period", monthLabel(d.month)],
              ["Invoice", (d.invoiceNumber ?? "").trim()],
              ["Prepared", new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })],
            ]}
          />

          {d.lines.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
              No lines yet. Pick entries on the left and group them.
            </div>
          ) : (
            <table style={reportTable}>
              <thead>
                <tr>
                  <th style={{ ...reportTh, textAlign: "left" }}>Description</th>
                  <th style={{ ...reportTh, textAlign: "right", width: 90 }}>Hours</th>
                  {d.showRates !== false && <th style={{ ...reportTh, textAlign: "right", width: 90 }}>Rate</th>}
                  {d.showRates !== false && <th style={{ ...reportTh, textAlign: "right", width: 100 }}>Amount</th>}
                  <th className="no-print" style={{ ...reportTh, width: 96 }} />
                </tr>
              </thead>
              <tbody>
                {d.lines.map((l, i) => {
                  const logged = loggedSeconds(l, ctx.byId) / 3600;
                  const billed = billedHours(l, d, ctx.byId);
                  const rate = lineRate(l, ctx.byId);
                  const handSet = typeof l.hours === "number";
                  const empty = !printable.includes(l);
                  const expanded = openLine === l.id;
                  return [
                    <tr key={l.id} className={empty ? "no-print" : undefined} style={empty ? { opacity: 0.55 } : undefined}>
                      <td style={cell}>
                        <input
                          className="no-print"
                          value={l.description}
                          onChange={(e) => updateLine(l.id, { description: e.target.value })}
                          placeholder="Describe this work"
                          aria-label="Line description"
                          style={lineInput}
                        />
                        <span className="print-only">{l.description}</span>
                        <button className="no-print" onClick={() => setOpenLine(expanded ? null : l.id)} style={linkBtn}>
                          {l.entryIds.length} {l.entryIds.length === 1 ? "entry" : "entries"} {expanded ? "▾" : "▸"}
                        </button>
                      </td>
                      <td style={{ ...cell, textAlign: "right" }}>
                        <HoursField value={billed} onCommit={(h) => updateLine(l.id, { hours: h })} />
                        <span className="print-only mono">{hrs(billed)}</span>
                        <span className="no-print" style={{ display: "block", fontSize: 11, color: handSet ? "var(--accent)" : "var(--muted)", whiteSpace: "nowrap" }}>
                          {handSet
                            ? <>set by hand · <button onClick={() => updateLine(l.id, { hours: null })} style={linkBtn}>reset</button></>
                            : `logged ${hrs(logged)}`}
                        </span>
                      </td>
                      {d.showRates !== false && (
                        <td style={{ ...cell, textAlign: "right" }} className="mono">{rate === null ? "—" : cents(rate, currency)}</td>
                      )}
                      {d.showRates !== false && (
                        <td style={{ ...cell, textAlign: "right" }} className="mono">{cents(lineAmount(l, d, ctx.byId), currency)}</td>
                      )}
                      <td className="no-print" style={{ ...cell, whiteSpace: "nowrap", textAlign: "right" }}>
                        <button onClick={() => shiftLine(l.id, -1)} disabled={i === 0} aria-label="Move line up" style={iconBtn}>↑</button>
                        <button onClick={() => shiftLine(l.id, 1)} disabled={i === d.lines.length - 1} aria-label="Move line down" style={iconBtn}>↓</button>
                        {d.lines.length > 1 && (
                          <select value="" onChange={(e) => e.target.value && mergeLine(l.id, e.target.value)} title="Merge into another line" aria-label="Merge into another line" style={{ ...iconBtn, width: 24, padding: 0, appearance: "none", textAlign: "center" }}>
                            <option value="">⇢</option>
                            {d.lines.filter((x) => x.id !== l.id).map((x) => <option key={x.id} value={x.id}>Merge into: {x.description || "Untitled line"}</option>)}
                          </select>
                        )}
                        <button onClick={() => removeLine(l.id)} title="Remove line — its entries go back to the left" aria-label="Remove line" style={iconBtn}>✕</button>
                      </td>
                    </tr>,
                    expanded && (
                      <tr key={`${l.id}-entries`} className="no-print">
                        <td colSpan={d.showRates !== false ? 5 : 3} style={{ ...cell, background: "var(--surface2)", padding: "6px 10px" }}>
                          {l.entryIds.length === 0 && <span style={{ fontSize: 12, color: "var(--muted)" }}>No entries on this line.</span>}
                          {l.entryIds.map((id) => {
                            const e = ctx.byId.get(id);
                            if (!e) return null;
                            return (
                              <div key={id} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5, padding: "3px 0" }}>
                                <span style={{ width: 70, color: "var(--muted)" }}>{shortDay(entryDayKey(e))}</span>
                                <span style={{ flex: 1, minWidth: 0 }}>{ctx.label(e)}</span>
                                <span className="mono">{clock(e.durationSeconds)}</span>
                                <button
                                  onClick={() => updateLine(l.id, { entryIds: l.entryIds.filter((x) => x !== id) })}
                                  title="Take off this line"
                                  style={iconBtn}
                                >✕</button>
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          )}

          {printable.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 28, marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--line2)", fontSize: 13 }}>
              <span><span style={{ color: "var(--muted)" }}>Total hours </span><strong className="mono">{hrs(totals.billedHours)}</strong></span>
              {d.showRates !== false && totals.amount > 0 && (
                <span><span style={{ color: "var(--muted)" }}>Total </span><strong className="mono">{cents(totals.amount, currency)}</strong></span>
              )}
            </div>
          )}
          {printable.length > 0 && Math.abs(totals.billedHours - totals.loggedHours) >= 0.005 && (
            <div className="no-print" style={{ textAlign: "right", fontSize: 11.5, color: "var(--muted)", marginTop: 6 }}>
              Logged {hrs(totals.loggedHours)} h · billing {hrs(totals.billedHours)} h
              ({totals.billedHours > totals.loggedHours ? "+" : "−"}{hrs(Math.abs(totals.billedHours - totals.loggedHours))} from rounding{d.lines.some((l) => typeof l.hours === "number") ? " and hours set by hand" : ""})
            </div>
          )}

          <ReportFooter />
        </div>
      </div>
    </>
  );
}

/**
 * The billed hours on a line. Edited as text and committed on blur or Enter:
 * reformatting to two decimals on every keystroke made "2.5" impossible to type.
 * Clearing the box goes back to the rounded logged hours.
 */
function HoursField({ value, onCommit }: { value: number; onCommit: (hours: number | null) => void }) {
  const [text, setText] = useState<string | null>(null);
  const commit = () => {
    if (text === null) return;
    const t = text.trim();
    const n = Number(t);
    if (t === "") onCommit(null);
    else if (Number.isFinite(n) && n >= 0 && n !== value) onCommit(Math.round(n * 10000) / 10000);
    setText(null);
  };
  return (
    <input
      className="no-print mono"
      inputMode="decimal"
      value={text ?? hrs(value)}
      onChange={(e) => setText(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setText(null); }}
      aria-label="Hours billed"
      title="Hours billed — type a figure to set it by hand"
      style={{ ...lineInput, width: 72, textAlign: "right" }}
    />
  );
}

/** "Oct 12" */
function shortDay(iso: string): string {
  const [y, m, day] = iso.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const bar: React.CSSProperties = {
  position: "sticky", top: 0, zIndex: 2, background: "var(--surface)",
  borderBottom: "1px solid var(--line)", padding: "14px 24px",
  display: "flex", flexDirection: "column", gap: 10,
};
const cell = reportTd;
const input: React.CSSProperties = {
  padding: "7px 10px", borderRadius: 8, fontSize: 12.5,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
const lineInput: React.CSSProperties = {
  width: "100%", border: "1px solid transparent", borderRadius: 6, padding: "3px 5px", margin: "-3px -5px",
  background: "transparent", color: "var(--ink)", fontSize: 13, fontFamily: "inherit",
};
const inline: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6 };
const linkBtn: React.CSSProperties = {
  border: "none", background: "none", padding: 0, color: "var(--muted)", fontSize: 11.5, cursor: "pointer", textDecoration: "underline",
};
const iconBtn: React.CSSProperties = {
  width: 24, height: 24, border: "none", background: "none", color: "var(--muted2)", cursor: "pointer", fontSize: 13, padding: 0,
};
const primary: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
};
const ghost: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
};
const disabledStyle: React.CSSProperties = { opacity: 0.5, cursor: "not-allowed" };
const notice: React.CSSProperties = {
  display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
  padding: "10px 12px", borderRadius: 10, background: "var(--accentTint2)", border: "1px solid var(--accentLine)",
};
const badge: React.CSSProperties = {
  fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "var(--tint)", color: "var(--ink3)",
};
const badgeDone: React.CSSProperties = { background: "var(--goodTint)", color: "var(--goodInk)" };
